import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Eye, Code2, Database, Terminal, Download, Files, Search, MousePointer2 } from 'lucide-react';
import { Button, cn } from '@ideoz/ui';
import { workbenchStore } from '../stores/workbench';
import { editorStore, selectFile } from '../stores/editor';
import { FileTree } from './FileTree';
import { EditorPanel } from './EditorPanel';
import { TerminalTabs } from './TerminalTabs';
import { FileSearch } from './FileSearch';
import { SearchReplacePanel } from './SearchReplacePanel';
import { downloadZip } from '../utils/downloadZip';
import { wcPerfMark, wcPerfMeasure } from '../utils/workbenchPerf';
import {
  deriveWorkbenchLoadingPhase,
  type WorkbenchLoadingPhase,
} from '../utils/workbenchLoadingPhase';
import { WORKBENCH_FIX_PREVIEW_CRASH_EVENT } from '../utils/previewCrashFix';
import { compileErrorsToCrash, unsupportedPackagesMessage } from '../utils/compileErrorFix';
import { WORKBENCH_BUILD_MISSING_VERSION_EVENT } from '../utils/workbenchEvents';
import { recoverPreviewEnvironment } from '../webcontainer/index';

const PREVIEW_SILENT_RECOVERY_MS = 30_000;
import { executeBuild } from '../runtime/tool-adapters/build';
import { getMergedFilePaths, switchTemplate } from '../utils/mergedFiles';
import { activeTemplateStore } from '../templates/headlessTemplate';
import { hotEditStore, takeEditModeHold } from '../stores/hotEdit';
import { useHotEditBridge } from '../hooks/useHotEditBridge';
import { HotEditPanel } from './hot-edit/HotEditPanel';
import { ListDataGrid } from './hot-edit/ListDataGrid';
import { deviceById, deviceFrameLayout, fitZoom } from './devices';
import { DeviceBody, DeviceScreenHardware } from './DeviceHardware';
import { deviceIdForTemplate, usePrototypeTemplate } from '../templates/usePrototypeTemplate';
import { EditToolbar } from './hot-edit/EditToolbar';
import { SmartPrompt } from './hot-edit/SmartPrompt';
import { HintCard } from './hot-edit/HintCard';
import { EditorSurfaceStyles } from './hot-edit/surface';
import { PendingChangesBar } from './hot-edit/PendingChangesBar';
import { LeavePendingPrompt } from './hot-edit/LeavePendingPrompt';
import { usePreviewFrameBox } from './hot-edit/usePreviewFrameBox';
import { EditModeHint } from './hot-edit/EditModeHint';
import { clearHotEditHistory } from '../preview-bridge/hotEditHistory';
import { isSaveShortcut } from '../utils/editorSaveKey';
import { sourceFileForPreviewStack } from '../preview/previewSourceFile';
import { appDataStore } from '../stores/appData';
import { useAppDataBridge } from '../hooks/useAppDataBridge';

type CodeViewMode = 'preview' | 'code' | 'data';
type SidebarView = 'explorer' | 'search';

const DEV_PREVIEW_SESSION_KEY = 'ideozWorkbenchDevPreview';

function readDevPreviewSession(): boolean {
  if (typeof globalThis.sessionStorage === 'undefined') return false;
  try {
    return globalThis.sessionStorage.getItem(DEV_PREVIEW_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDevPreviewSession(enabled: boolean): void {
  try {
    if (enabled) globalThis.sessionStorage?.setItem(DEV_PREVIEW_SESSION_KEY, '1');
    else globalThis.sessionStorage?.removeItem(DEV_PREVIEW_SESSION_KEY);
  } catch {
    /* noop */
  }
}

export interface WorkbenchProps {
  projectName?: string;
  /** Optional loading UI for the preview tab (e.g. PrototyperLoadingAnimation + caption). */
  /**
   * `variant: 'generating'` is only for the AI actively writing files — pair it with an
   * illustrated/animated treatment. Every other reason (installing deps, building,
   * type-checking, reconnecting to a saved snapshot) is `'plain'` — a mechanical wait, not
   * design reasoning, so it should read as a plain spinner + caption, not the same "AI is
   * thinking" narrative.
   */
  renderPreviewLoading?: (ctx: {
    message: string;
    variant: 'generating' | 'plain';
    /** What the preview is waiting on, so the host can show a build's own progress. */
    phase: WorkbenchLoadingPhase;
  }) => React.ReactNode;
  /**
   * When true (e.g. prototyper embedded in conversation), omit the right Tweaks rail — inspector shows in chat column instead.
   */
  /**
   * Extra content for this toolbar so the embedding panel can merge its own header
   * into it (one bar instead of two). `headerLeftSlot` renders at the far left
   * (before the view toggle) — e.g. a title; `headerRightSlot` at the far right
   * (after Edit/Rebuild/terminal) — e.g. fullscreen · Publish · close.
   */
  headerLeftSlot?: React.ReactNode;
  headerRightSlot?: React.ReactNode;
  /** Rendered at the very start of the right cluster (before the view toggle). */
  headerRightLeadSlot?: React.ReactNode;
  /**
   * The startup's live data (User Story 598), shown as a third view next to Preview and Code.
   * Without it there is no Data button. It is drawn over the preview, which stays loaded, so
   * the preview shows the changes as soon as the founder switches back.
   */
  dataView?: React.ReactNode;
}

/** A hairline between two groups of header controls, so the row reads as three things. */
function HeaderDivider(): React.ReactElement {
  return <span className="mx-0.5 h-5 w-px shrink-0 bg-border/60" aria-hidden />;
}

export function Workbench({
  projectName = 'prototype',
  renderPreviewLoading,
  headerLeftSlot,
  headerRightSlot,
  headerRightLeadSlot,
  dataView,
}: WorkbenchProps) {
  const [codeViewMode, setCodeViewMode] = useState<CodeViewMode>('preview');
  const openDataViewRequest = useStore(appDataStore.openDataView);
  useEffect(() => {
    if (openDataViewRequest > 0 && dataView) {
      hotEditStore.mode.set(false);
      hotEditStore.selection.set(null);
      setCodeViewMode('data');
    }
    // Only a new request switches; the view itself is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDataViewRequest]);
  const [sidebarView, setSidebarView] = useState<SidebarView>('explorer');
  const [terminalPanelOpen, setTerminalPanelOpen] = useState(false);
  const [fileSearchOpen, setFileSearchOpen] = useState(false);
  const [fileTreeWidth, setFileTreeWidth] = useState(280);
  const [terminalHeight, setTerminalHeight] = useState(220);
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);

  const codeViewRef = useRef<HTMLDivElement>(null);
  const previewIframePerfStartRef = useRef<string | null>(null);

  const [rebuildBusy, setRebuildBusy] = useState(false);
  /** Fast preview loads the running Vite dev server before production build succeeds. */
  const [preferFastDevPreview, setPreferFastDevPreview] = useState(readDevPreviewSession);
  /** True after the preview iframe fires `load` for the current `previewUrl`. */
  const [previewIframeLoaded, setPreviewIframeLoaded] = useState(false);
  /**
   * Fallback for `bridgeReady` below: if the injected preview-bridge script never checks in
   * (e.g. the generated app throws before mounting), stop waiting on it after a while so a
   * broken preview still becomes visible instead of spinning forever.
   */
  const [bridgeReadyTimedOut, setBridgeReadyTimedOut] = useState(false);
  const previewUrl = useStore(workbenchStore.previewUrl);
  const snapshotPreviewReady = useStore(workbenchStore.snapshotPreviewReady);
  const buildStatus = useStore(workbenchStore.buildStatus);
  const isStreaming = useStore(workbenchStore.isStreaming);
  const clientToolsBusy = useStore(workbenchStore.clientToolsBusy);
  const depsInstalled = useStore(workbenchStore.depsInstalled);
  const fileConflicts = useStore(workbenchStore.fileConflicts);
  const manualContextTruncationWarning = useStore(workbenchStore.manualContextTruncationWarning);
  /**
   * A turn is one wait, not a slideshow. While it runs, files land one at a time and the server
   * build has the last word, so the panel shows the turn's own progress — "Generating your
   * prototype…", then "Building and checking your prototype…" — and the preview appears when the
   * turn is done. A tab-compiled preview could technically be shown throughout, but watching
   * half-written pages flicker past is not what the founder asked the turn for.
   *
   * `buildStatus === 'success'` releases it: the model may still be finishing a silent
   * continuation, and that no longer has anything to do with what is on screen.
   */
  const previewBlockedByClientTools =
    (isStreaming && buildStatus !== 'success') || clientToolsBusy || buildStatus === 'building';
  const webcontainer = useStore(workbenchStore.webcontainer);
  const files = useStore(workbenchStore.files);
  /** Reported from inside the iframe by `src/ideoz/renderSignal.ts` (see `useHotEditBridge`). */
  const previewRendered = useStore(workbenchStore.previewRendered);
  const previewCrash = useStore(workbenchStore.previewCrash);
  /** Light preview (F2): the in-tab compiler publishes these; both are empty on the WebContainer path. */
  const previewCompileErrors = useStore(workbenchStore.previewCompileErrors);
  const previewUnsupportedPackages = useStore(workbenchStore.previewUnsupportedPackages);
  const previewCompiling = useStore(workbenchStore.previewCompiling);
  const previewShowsTemplatePlaceholder = useStore(workbenchStore.previewShowsTemplatePlaceholder);
  const snapshotRestorePending = useStore(workbenchStore.snapshotRestorePending);
  const continuingBuild = useStore(workbenchStore.continuingBuild);
  /** Bumped by "Reload preview" to remount the iframe on the same URL. */
  const [previewReloadNonce, setPreviewReloadNonce] = useState(0);
  const devServerStatus = useStore(workbenchStore.devServerStatus);
  /** A fast snapshot switch detaches the page while it writes files (P6.4d-2). */
  const previewSwitching = useStore(workbenchStore.previewSwitching);
  const previewServerReady = devServerStatus === 'ready' && !previewSwitching;
  /**
   * Bumped each time the dev server comes back up (snapshot restore, switching prototypes,
   * restart, recovery). The iframe is keyed on it and not mounted while the server is down, so
   * each server start gets a fresh page whose render signal decides when to reveal it. The old
   * page used to stay visible through the gap, showing Vite's lost-connection black screen or
   * WebContainer's "port 3111" page.
   */
  const [previewServerEpoch, setPreviewServerEpoch] = useState(0);
  const wasPreviewServerReadyRef = useRef(previewServerReady);
  useEffect(() => {
    if (previewServerReady && !wasPreviewServerReadyRef.current) {
      setPreviewServerEpoch((n) => n + 1);
    }
    wasPreviewServerReadyRef.current = previewServerReady;
  }, [previewServerReady]);

  /**
   * Moved ahead of the preview-loading gate below: `bridgeReady` only flips true once the
   * injected preview-bridge script posts `IDEOZ_EDIT_READY` from inside the iframe, which only
   * happens after the generated React app has actually mounted — a far more honest "is there
   * something to look at yet" signal than the iframe's native `load` event (which fires once
   * the tiny HTML shell loads, long before Vite has compiled and executed the app's JS bundle).
   */
  const { previewIframeRef, bridgeReady, toggleHotEditMode } = useHotEditBridge({
    previewUrl,
    iframeLoaded: previewIframeLoaded,
  });
  useAppDataBridge(previewIframeRef as React.RefObject<HTMLIFrameElement | null>);

  // Ctrl+P / Cmd+P → quick open
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        hotEditStore.mode.set(false);
        hotEditStore.selection.set(null);
        if (codeViewMode !== 'code') setCodeViewMode('code');
        setFileSearchOpen(true);
      }
      // Edits sync on their own; Ctrl+S outside the editor (file tree, tabs) would otherwise open
      // the browser's "Save page" dialog. Inside the editor, `EditorPanel`'s binding handles it.
      if (codeViewMode === 'code' && isSaveShortcut(e)) {
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        hotEditStore.mode.set(false);
        hotEditStore.selection.set(null);
        if (codeViewMode !== 'code') setCodeViewMode('code');
        setSidebarView('search');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [codeViewMode]);

  const activeTemplate = useStore(activeTemplateStore);
  const fileList = useMemo(() => getMergedFilePaths(), [files, activeTemplate]);

  const unsyncedEditCount = useMemo(
    () => Object.values(files).filter((f) => f.modified).length,
    [files]
  );

  const conflictCount = useMemo(() => Object.keys(fileConflicts).length, [fileConflicts]);

  const prodPreviewInteractive =
    !!previewUrl && depsInstalled && buildStatus === 'success' && !previewBlockedByClientTools;

  /** Snapshot reopen: dev server + restored files — no second production build required. */
  const snapshotRestoredPreviewInteractive =
    snapshotPreviewReady && !!previewUrl && depsInstalled && !previewBlockedByClientTools;

  /** Optional: show iframe from `npm run dev` before vite build+tsc succeeds. */
  const devPreviewInteractive =
    preferFastDevPreview && !!previewUrl && depsInstalled && !previewBlockedByClientTools;

  const previewInteractive = preferFastDevPreview
    ? devPreviewInteractive
    : prodPreviewInteractive || snapshotRestoredPreviewInteractive;

  /** Which condition made `previewInteractive` true — lets the loading-phase ladder continue
   * the phrase already on screen instead of jumping to an unrelated one once it's waiting on
   * the iframe/bridge (see `deriveWorkbenchLoadingPhase`). */
  const previewInteractiveReason: 'success' | 'snapshot' | 'fast-dev' | null = preferFastDevPreview
    ? devPreviewInteractive
      ? 'fast-dev'
      : null
    : prodPreviewInteractive
      ? 'success'
      : snapshotRestoredPreviewInteractive
        ? 'snapshot'
        : null;

  /** Reset iframe "loaded" whenever the URL or gate changes so we show loading until the next load event. */
  useEffect(() => {
    setPreviewIframeLoaded(false);
    previewIframePerfStartRef.current = null;
  }, [previewUrl, previewInteractive, previewServerReady, previewServerEpoch]);

  useEffect(() => {
    if (!previewInteractive || !previewUrl) {
      previewIframePerfStartRef.current = null;
      return;
    }
    const suffix =
      typeof globalThis.performance?.now === 'function' ? `${performance.now()}` : `${Date.now()}`;
    const markName = `wc:preview:iframe:nav:start:${suffix}`;
    previewIframePerfStartRef.current = markName;
    wcPerfMark(markName);
  }, [previewInteractive, previewUrl]);

  const showPreviewLoader =
    !previewInteractive &&
    !!(
      previewBlockedByClientTools ||
      !depsInstalled ||
      !previewUrl ||
      (!snapshotPreviewReady &&
        !preferFastDevPreview &&
        buildStatus !== 'success' &&
        (isStreaming || !!previewUrl))
    );

  const handleRebuild = useCallback(async () => {
    const wc = workbenchStore.webcontainer.get();
    if (!wc || rebuildBusy) return;
    setRebuildBusy(true);
    workbenchStore.buildStatus.set('building');
    try {
      const result = await executeBuild(wc);
      workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
      if (result.environment) void recoverPreviewEnvironment('workspace-unresponsive');
      if (result.ok) {
        setPreviewIframeLoaded(false);
        workbenchStore.hasUnbuiltEdits.set(false);
      }
    } catch {
      workbenchStore.buildStatus.set('error');
    } finally {
      setRebuildBusy(false);
    }
  }, [rebuildBusy]);

  const handleDownloadZip = useCallback(() => {
    downloadZip(projectName);
  }, [projectName]);

  const toggleTerminal = useCallback(() => {
    if (codeViewMode !== 'code') {
      setCodeViewMode('code');
      setTerminalPanelOpen(true);
      return;
    }
    setTerminalPanelOpen((o: boolean) => !o);
  }, [codeViewMode]);

  const handleFileTreeResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = fileTreeWidth;
      setIsDraggingPanel(true);
      const onMove = (ev: MouseEvent) => {
        const container = codeViewRef.current;
        const maxWidth = container ? container.offsetWidth * 0.5 : 600;
        setFileTreeWidth(Math.min(Math.max(160, startWidth + (ev.clientX - startX)), maxWidth));
      };
      const onUp = () => {
        setIsDraggingPanel(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [fileTreeWidth]
  );

  const handleTerminalResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startY = e.clientY;
      const startHeight = terminalHeight;
      setIsDraggingPanel(true);
      const onMove = (ev: MouseEvent) => {
        setTerminalHeight(Math.min(500, Math.max(80, startHeight + (startY - ev.clientY))));
      };
      const onUp = () => {
        setIsDraggingPanel(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [terminalHeight]
  );

  /**
   * `previewIframeLoaded` alone isn't enough to reveal the iframe — that's just the native
   * `load` event, which fires once the (tiny) HTML shell loads, long before Vite has compiled
   * and executed the app's JS bundle. `bridgeReady` (from `useHotEditBridge`, above) only flips
   * true once the injected preview-bridge script posts back from inside the iframe, which only
   * happens after the generated React app has actually mounted — a far more honest "is there
   * something to look at yet" signal. `bridgeReadyTimedOut` is the escape hatch for a generated
   * app that errors before it can mount, or a dev server that never actually serves the iframe's
   * `load` event at all (a slow/stalled WebContainer network layer) — it stops waiting after 15s
   * and shows the (possibly broken) iframe rather than spinning forever. Keyed off
   * `previewInteractive` (the moment we're eligible to show *something*), not
   * `previewIframeLoaded` — otherwise a `load` event that never fires at all means this timer
   * never even starts, and "Loading your prototype preview…" sits there with no escape.
   */
  useEffect(() => {
    setBridgeReadyTimedOut(false);
    // Counts from when the server is back, so a restore's stopped server does not use it up.
    if (!previewInteractive || !previewServerReady) return undefined;
    const timer = setTimeout(() => setBridgeReadyTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, [previewInteractive, previewUrl, previewReloadNonce, previewServerReady, previewServerEpoch]);

  /**
   * No `IDEOZ_EDIT_READY`, `IDEOZ_EDIT_RENDERED` or `IDEOZ_EDIT_CRASHED` long after the preview
   * became eligible means the app's JS never ran at all: the dev server or the WebContainer is
   * the problem, not the generated code. Longer than the 15s reveal so a slow first Vite compile
   * on a weak device is not mistaken for a dead environment.
   */
  useEffect(() => {
    if (!previewInteractive || !previewServerReady) return undefined;
    if (bridgeReady || previewRendered || previewCrash) return undefined;
    const timer = setTimeout(() => {
      void recoverPreviewEnvironment('preview-silent');
    }, PREVIEW_SILENT_RECOVERY_MS);
    return () => clearTimeout(timer);
  }, [
    previewInteractive,
    previewUrl,
    previewReloadNonce,
    previewServerReady,
    previewServerEpoch,
    bridgeReady,
    previewRendered,
    previewCrash,
  ]);

  const loadingPhase = deriveWorkbenchLoadingPhase({
    depsInstalled,
    buildStatus,
    isStreaming,
    clientToolsBusy,
    previewUrl,
    snapshotPreviewReady,
    preferFastDevPreview,
    previewInteractive,
    previewInteractiveReason,
    previewIframeLoaded,
    // `IDEOZ_EDIT_READY` comes from `initPreviewBridge()` in `main.tsx`; `IDEOZ_EDIT_RENDERED`
    // comes from the render signal. Either proves the app's JS ran; a render crash that
    // follows READY arrives as `previewCrash` within `CRASH_SETTLE_MS` (250ms).
    bridgeReady: bridgeReady || previewRendered,
    bridgeReadyTimedOut,
    previewCrashed: previewCrash != null,
    previewShowsTemplatePlaceholder,
    snapshotRestorePending,
    continuingBuild,
    previewServerReady,
  });
  /**
   * Keep the loading overlay over the iframe until the page has actually rendered — for a live
   * build (`waiting-for-app-mount`) and for a restored or switched prototype
   * (`reconnecting-snapshot`), which used to reveal the iframe before anything rendered.
   */
  const previewWaitingForIframe =
    previewInteractive &&
    !loadingPhase.isReady &&
    (loadingPhase.phase === 'waiting-for-app-mount' ||
      loadingPhase.phase === 'reconnecting-snapshot' ||
      // The starter page behind a pressed Continue is not the prototype; keep it covered.
      loadingPhase.phase === 'continuing-build');
  const previewCrashedView = loadingPhase.phase === 'app-crashed' && previewCrash != null;
  const previewNoSavedVersionView = loadingPhase.phase === 'no-saved-version';
  const previewIframeHidden =
    previewWaitingForIframe || previewCrashedView || previewNoSavedVersionView;

  const buildMissingVersion = useCallback(() => {
    window.dispatchEvent(new CustomEvent(WORKBENCH_BUILD_MISSING_VERSION_EVENT));
  }, []);

  const reloadPreview = useCallback(() => {
    workbenchStore.previewCrash.set(null);
    workbenchStore.previewRendered.set(false);
    setPreviewIframeLoaded(false);
    setPreviewReloadNonce((n) => n + 1);
  }, []);

  const fixPreviewCrash = useCallback(() => {
    const crash = workbenchStore.previewCrash.get();
    if (!crash) return;
    // Under the light preview the crash names a blob URL, not a file; trace it to the file.
    const file =
      crash.file && !crash.file.startsWith('blob:')
        ? crash.file
        : (sourceFileForPreviewStack(crash.stack) ?? crash.file);
    window.dispatchEvent(
      new CustomEvent(WORKBENCH_FIX_PREVIEW_CRASH_EVENT, { detail: { ...crash, file } })
    );
  }, []);

  /** A compile failure reaches the model through the same path as a runtime crash. */
  const fixCompileErrors = useCallback(() => {
    const crash = compileErrorsToCrash(workbenchStore.previewCompileErrors.get());
    if (!crash) return;
    window.dispatchEvent(new CustomEvent(WORKBENCH_FIX_PREVIEW_CRASH_EVENT, { detail: crash }));
  }, []);

  /**
   * Shown over the last working preview rather than instead of it: the founder keeps the screen
   * they had while the error explains why it stopped updating. Hidden during a recompile, so a
   * half-typed edit does not flash an error.
   */
  const compileErrorsVisible = previewCompileErrors.length > 0 && !previewCompiling;
  const unsupportedNotice = unsupportedPackagesMessage(previewUnsupportedPackages);

  /**
   * Publish "is there actually something to look at" to the shared store — components outside
   * this tree (the panel toolbar deciding when to reveal View Insights / Publish) have no other
   * way to see this, which is otherwise only derived from local state here.
   */
  useEffect(() => {
    workbenchStore.previewReady.set(loadingPhase.isReady);
  }, [loadingPhase.isReady]);

  const hotEditMode = useStore(hotEditStore.mode);
  const hotEditApi = useStore(hotEditStore.bridgeApi);
  const [panelOpen, setPanelOpen] = useState(false);
  // The toolbar is drawn over the preview, so it needs the iframe's box in the page.
  const { frameRef, frameBox } = usePreviewFrameBox(hotEditMode);

  /**
   * The device the preview is framed in (US 359, phase one). The frame is chrome: the prototype
   * inside is a plain full-bleed app, so `position: fixed` and portalled overlays behave exactly
   * as they will on the founder's phone.
   *
   * Decided by the prototype, not picked: a mobile one is shown on an iPhone 17 and everything
   * else at desktop width. A picker let the frame disagree with what was built - a webapp on a
   * phone, or a Framework7 app stretched across a desktop - and neither is a preview of anything.
   */
  const prototypeTemplate = usePrototypeTemplate();
  const device = deviceById(deviceIdForTemplate(prototypeTemplate));
  // The file tree and the editor list the active template's files, so the workspace follows the
  // prototype as soon as its files say which one it is, not only when a preview restarts.
  useEffect(() => {
    switchTemplate(prototypeTemplate);
  }, [prototypeTemplate]);
  const [deviceStageBox, setDeviceStageBox] = useState<{ width: number; height: number } | null>(null);
  /**
   * Measures the stage the device is fitted into, whichever stage element is mounted.
   *
   * A callback ref, not an effect: the stage only exists while the preview is ready, so it is
   * remounted on the way back from the code view, and an effect keyed on the device kept
   * observing the old node. A hidden stage measures 0×0, which is ignored - fitted to that, the
   * phone came back at half size (27 Sep 2026).
   */
  const deviceStageObserverRef = useRef<ResizeObserver | null>(null);
  const deviceStageRef = useCallback((node: HTMLDivElement | null) => {
    deviceStageObserverRef.current?.disconnect();
    deviceStageObserverRef.current = null;
    if (!node) return;
    const measure = () => {
      if (node.clientWidth > 0 && node.clientHeight > 0) {
        setDeviceStageBox({ width: node.clientWidth, height: node.clientHeight });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    deviceStageObserverRef.current = observer;
  }, []);
  useEffect(() => () => deviceStageObserverRef.current?.disconnect(), []);

  const deviceZoom = device.width && deviceStageBox
    ? fitZoom(device, deviceStageBox.height, deviceStageBox.width)
    : 1;
  // The editor injects the safe areas into the preview (below), so the island and the home
  // indicator sit over strips the app keeps free.
  const deviceFrame = deviceFrameLayout(device, deviceZoom, true);

  /**
   * The device's safe-area insets, pushed into the preview document itself.
   *
   * `env(safe-area-inset-*)` is 0 in a desktop iframe, so without this the notch and home
   * indicator would exist on a real phone and not in the editor the founder reviewed the layout
   * in. The light preview is a blob document from this origin, so its variables can be set
   * directly; the WebContainer preview is cross-origin and throws, which is why this is guarded
   * rather than conditional — there is nothing to do for that path and nothing to report.
   */
  useEffect(() => {
    const iframe = (previewIframeRef as React.RefObject<HTMLIFrameElement>).current;
    if (!iframe) return;
    const apply = () => {
      try {
        const root = iframe.contentDocument?.documentElement;
        if (!root) return;
        if (device.safe) {
          root.style.setProperty('--ideoz-safe-top', `${device.safe.top}px`);
          root.style.setProperty('--ideoz-safe-bottom', `${device.safe.bottom}px`);
        } else {
          root.style.removeProperty('--ideoz-safe-top');
          root.style.removeProperty('--ideoz-safe-bottom');
        }
      } catch {
        // Cross-origin preview: the device frame still sizes it, only the insets are unavailable.
      }
    };
    apply();
    iframe.addEventListener('load', apply);
    return () => iframe.removeEventListener('load', apply);
  }, [device, previewIframeRef, previewUrl, previewIframeLoaded]);

  // Undo covers hot edits only: a chat turn starts from here, so earlier hot edits are final.
  useEffect(() => {
    if (isStreaming) clearHotEditHistory();
  }, [isStreaming]);

  useEffect(() => {
    // A reload the founder's own Apply asked for keeps them where they were: the bridge picks
    // their element up again by selector path when the new page checks in.
    if (takeEditModeHold()) return;
    hotEditStore.mode.set(false);
    hotEditStore.selection.set(null);
  }, [previewUrl]);

  useEffect(() => {
    const onOpenCode = () => setCodeViewMode('code');
    window.addEventListener('ideoz-workbench-open-code', onOpenCode);
    return () => window.removeEventListener('ideoz-workbench-open-code', onOpenCode);
  }, []);

  const previewBody = (
    <div className="relative h-full min-h-0 flex flex-col bg-white">
      {previewInteractive ? (
        <div className="relative flex-1 min-h-0 w-full">
          {previewWaitingForIframe && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white px-4 text-center"
              aria-busy="true"
              aria-live="polite"
            >
              {renderPreviewLoading ? (
                renderPreviewLoading({
                  message: loadingPhase.message,
                  variant: loadingPhase.variant,
                  phase: loadingPhase.phase,
                })
              ) : (
                <p className="text-sm text-muted-foreground">{loadingPhase.message}</p>
              )}
            </div>
          )}
          {previewCrashedView && previewCrash && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white px-6 text-center"
              role="alert"
            >
              <p className="text-lg font-semibold text-red-600">
                {previewCrash.kind === 'compile'
                  ? "This screen couldn't be built"
                  : 'This screen crashed'}
              </p>
              <pre className="max-h-40 w-full max-w-lg overflow-auto whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-left text-xs text-slate-700">
                {previewCrash.message}
              </pre>
              {previewCrash.file && (
                <p className="max-w-lg break-all text-xs text-slate-500">{previewCrash.file}</p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button type="button" size="sm" onClick={fixPreviewCrash} disabled={isStreaming}>
                  Fix with AI
                </Button>
                <Button type="button" variant="secondary-gray" size="sm" onClick={reloadPreview}>
                  Reload preview
                </Button>
              </div>
            </div>
          )}
          {compileErrorsVisible && (
            <div
              className="absolute inset-x-0 bottom-0 z-30 flex flex-col gap-2 border-t border-red-200 bg-white/95 p-4 shadow-lg backdrop-blur"
              role="alert"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-red-600">
                  {previewCompileErrors.length === 1
                    ? "This change didn't compile"
                    : `${previewCompileErrors.length} problems stopped this change compiling`}
                </p>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" onClick={fixCompileErrors} disabled={isStreaming}>
                    Fix with AI
                  </Button>
                  <Button
                    type="button"
                    variant="secondary-gray"
                    size="sm"
                    onClick={() => setCodeViewMode('code')}
                  >
                    Open code
                  </Button>
                </div>
              </div>
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-left text-xs text-slate-700">
                {previewCompileErrors.slice(0, 5).join('\n')}
              </pre>
              <p className="text-xs text-slate-500">
                The preview still shows the last version that compiled.
              </p>
            </div>
          )}
          {unsupportedNotice && !compileErrorsVisible && (
            <div
              className="absolute inset-x-0 top-0 z-30 flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2"
              role="status"
            >
              <p className="text-xs text-amber-900">{unsupportedNotice}</p>
            </div>
          )}
          {previewNoSavedVersionView && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white px-6 text-center"
              role="status"
            >
              <p className="text-lg font-semibold text-slate-800">{loadingPhase.message}</p>
              <p className="max-w-md text-sm text-slate-600">
                The workspace only has the starter template. Build the prototype from the latest
                plan to see it here.
              </p>
              <Button type="button" size="sm" onClick={buildMissingVersion} disabled={isStreaming}>
                Build prototype
              </Button>
            </div>
          )}
          {previewServerReady && (
          <div
            ref={deviceStageRef}
            className={cn(
              'absolute inset-0',
              device.width && 'flex items-start justify-center overflow-auto bg-neutral-100 p-4 dark:bg-neutral-900',
            )}
          >
          <div
            style={
              device.width
                ? { width: deviceFrame.outer.width, height: deviceFrame.outer.height, flex: 'none' }
                : undefined
            }
            className={device.width ? 'relative' : 'contents'}
          >
          {device.width ? <DeviceBody layout={deviceFrame} /> : null}
          <iframe
            ref={previewIframeRef as React.RefObject<HTMLIFrameElement>}
            key={`${previewUrl}#${previewReloadNonce}#${previewServerEpoch}`}
            src={previewUrl}
            title="Preview"
            onLoad={() => {
              const start = previewIframePerfStartRef.current;
              if (start) {
                const end = `${start}:loaded`;
                wcPerfMark(end);
                wcPerfMeasure(start, end, 'wc:preview:iframeNavigate');
                previewIframePerfStartRef.current = null;
              }
              setPreviewIframeLoaded(true);
            }}
            style={
              device.width
                ? {
                    width: device.width,
                    height: device.height ?? undefined,
                    // Scaled from the top left so the frame's box and the iframe's agree —
                    // `getBoundingClientRect` reports the scaled box, which is what the hot-edit
                    // toolbar measures, so it keeps landing correctly.
                    transform: `scale(${deviceZoom})`,
                    transformOrigin: 'top left',
                    left: deviceFrame.screen.left,
                    top: deviceFrame.screen.top,
                    // Before the scale: this radius is in device pixels, like the rest of the box.
                    borderRadius: device.hardware?.screenRadius ?? 28,
                  }
                : undefined
            }
            className={cn(
              'border-none transition-opacity duration-150',
              device.width
                ? cn('absolute bg-white', !device.hardware && 'shadow-xl')
                : 'absolute inset-0 h-full w-full min-h-[240px]',
              previewIframeHidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
            )}
            aria-hidden={previewIframeHidden}
            // No sandbox: WebContainer already isolates execution. A strict sandbox can break Vite's
            // dev client/HMR and leave a blank white surface inside the iframe.
          />
          {device.width && !previewIframeHidden ? <DeviceScreenHardware layout={deviceFrame} /> : null}
          </div>
          </div>
          )}
        </div>
      ) : showPreviewLoader && renderPreviewLoading ? (
        <div className="flex flex-col items-center justify-center h-full gap-3 px-4 text-center">
          {renderPreviewLoading({
            message: loadingPhase.message,
            variant: loadingPhase.variant,
            phase: loadingPhase.phase,
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm px-6 gap-2">
          <p>{loadingPhase.message}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative flex flex-col h-full min-h-0 bg-surface text-foreground">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex-shrink-0 h-16">
        <div className="flex items-center gap-2 min-w-0">{headerLeftSlot}</div>

        <div className="flex items-center gap-1.5 shrink-0 min-w-0">
          {headerRightLeadSlot}
          <div
            className="inline-flex h-8 items-center rounded-md border border-border/50 bg-background/80 p-0.5 shadow-sm backdrop-blur-sm shrink-0"
            role="group"
            aria-label="Workbench view"
          >
            <button
              type="button"
              aria-label="Preview"
              aria-pressed={codeViewMode === 'preview'}
              onClick={() => setCodeViewMode('preview')}
              className={cn(
                'p-1.5 rounded transition-colors',
                codeViewMode === 'preview'
                  ? 'bg-muted text-foreground shadow-inner'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
            >
              <Eye size={16} />
            </button>
            <button
              type="button"
              aria-label="Code editor"
              aria-pressed={codeViewMode === 'code'}
              onClick={() => {
                hotEditStore.mode.set(false);
                hotEditStore.selection.set(null);
                setCodeViewMode('code');
                 setTerminalPanelOpen(true);
              }}
              className={cn(
                'p-1.5 rounded transition-colors',
                codeViewMode === 'code'
                  ? 'bg-muted text-foreground shadow-inner'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              )}
            >
              <Code2 size={16} />
            </button>
            {dataView ? (
              <button
                type="button"
                aria-label="Data"
                title="Your startup's data"
                aria-pressed={codeViewMode === 'data'}
                onClick={() => {
                  hotEditStore.mode.set(false);
                  hotEditStore.selection.set(null);
                  setCodeViewMode('data');
                }}
                className={cn(
                  'p-1.5 rounded transition-colors',
                  codeViewMode === 'data'
                    ? 'bg-muted text-foreground shadow-inner'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                <Database size={16} />
              </button>
            ) : null}
          </div>
          {isStreaming && (
            <span className="text-xs text-blue-light-600 animate-pulse whitespace-nowrap hidden sm:inline">
              Streaming…
            </span>
          )}
          {isStreaming && unsyncedEditCount > 0 && (
            <span
              className="text-xs text-amber-800 max-w-[160px] sm:max-w-[240px] truncate hidden md:inline"
              title="Manual edits are protected — AI writeFile calls on these paths will not overwrite your content."
            >
              {unsyncedEditCount} protected edit{unsyncedEditCount === 1 ? '' : 's'}
            </span>
          )}
          {conflictCount > 0 && (
            <span
              className="text-xs text-amber-900 font-medium max-w-[140px] sm:max-w-[200px] truncate hidden md:inline"
              title="Open conflicted files in the editor to accept or reject AI changes."
            >
              {conflictCount} conflict{conflictCount === 1 ? '' : 's'}
            </span>
          )}
          <HeaderDivider />
          <Button
            type="button"
            variant={hotEditMode ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-8 gap-1.5 px-3 text-xs',
              hotEditMode &&
                'bg-primary text-primary-foreground hover:bg-primary/90 border-primary shadow-sm'
            )}
            disabled={rebuildBusy || isStreaming || !previewInteractive || !previewIframeLoaded}
            onClick={() => void toggleHotEditMode()}
            title={
              bridgeReady
                ? 'Edit the prototype — click anything in the preview to change it'
                : 'Connecting preview inspect bridge…'
            }
          >
            <MousePointer2 size={14} aria-hidden />
            Edit
          </Button>
          {/* Rebuild is hidden: the light preview compiles in the tab on every save, so there
              is nothing for a founder to rebuild. `handleRebuild` stays for the WebContainer
              path and can be put back behind a flag if that path returns. */}
          {/* <button
            type="button"
            onClick={toggleTerminal}
            aria-label={
              codeViewMode === 'code' && terminalPanelOpen ? 'Hide terminal' : 'Show terminal'
            }
            aria-expanded={codeViewMode === 'code' && terminalPanelOpen}
            className={cn(
              'relative p-1.5 rounded-md border transition-colors',
              codeViewMode === 'code' && terminalPanelOpen
                ? 'border-border bg-background text-foreground'
                : 'border-border/50 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            )}
            title="Terminal"
          >
            <Terminal size={16} />
          
          </button> */}
          <HeaderDivider />
          <button
            type="button"
            aria-label="Download ZIP"
            onClick={handleDownloadZip}
            className="grid h-8 w-8 place-items-center rounded-md border border-border/50 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            title="Download ZIP"
          >
            <Download size={16} />
          </button>
          {headerRightSlot}
        </div>
      </div>

      {manualContextTruncationWarning ? (
        <div
          className="shrink-0 border-b border-amber-300/70 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-950"
          role="status"
        >
          {manualContextTruncationWarning}
        </div>
      ) : null}

      {hotEditMode ? <LeavePendingPrompt api={hotEditApi} /> : null}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-gray-50 dark:bg-gray-900 m-4">
        {codeViewMode !== 'code' ? (
          <div className="flex flex-1 min-h-0 overflow-hidden flex-row">
            <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
              {hotEditMode ? <EditModeHint /> : null}
              <div ref={frameRef} className="relative flex-1 min-h-0 overflow-hidden">
                {previewBody}
                {codeViewMode === 'data' && dataView ? (
                  <div className="absolute inset-0 z-40 overflow-auto bg-background" role="region" aria-label="Data">
                    {dataView}
                  </div>
                ) : null}
                {hotEditMode ? (
                  <>
                    <EditorSurfaceStyles />
                    <EditToolbar
                      api={hotEditApi}
                      frame={frameBox}
                      onMore={() => setPanelOpen((open) => !open)}
                    />
                    <SmartPrompt frame={frameBox} />
                    <HintCard frame={frameBox} />
                    <ListDataGrid />
                    <PendingChangesBar api={hotEditApi} frame={frameBox} />
                  </>
                ) : null}
              </div>
            </div>
            {hotEditMode && panelOpen ? (
              <HotEditPanel
                className="w-[300px] shrink-0 border-l border-border/60 max-md:hidden"
                onClose={() => setPanelOpen(false)}
              />
            ) : null}
          </div>
        ) : (
          <div ref={codeViewRef} className="flex flex-1 min-h-0 overflow-hidden">
            {/* Activity bar */}
            <div
              className="flex flex-col items-center gap-1 py-2 px-1 border-r border-border/40 shrink-0 glass-subtle w-11"
              aria-label="Sidebar activities"
            >
              <button
                type="button"
                aria-label="Explorer"
                aria-pressed={sidebarView === 'explorer'}
                onClick={() => setSidebarView('explorer')}
                title="Explorer"
                className={cn(
                  'p-2 rounded-md transition-colors',
                  sidebarView === 'explorer'
                    ? 'text-foreground bg-background/90 shadow-sm border border-border/50'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
              >
                <Files size={18} />
              </button>
              <button
                type="button"
                aria-label="Search"
                aria-pressed={sidebarView === 'search'}
                onClick={() => setSidebarView('search')}
                title="Search"
                className={cn(
                  'p-2 rounded-md transition-colors border-b-2 border-transparent',
                  sidebarView === 'search'
                    ? 'text-foreground bg-background/90 shadow-sm border border-border/50'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
              >
                <Search size={18} />
              </button>
            </div>

            {/* Sidebar panel */}
            <div
              className="border-r border-border/40 overflow-hidden bg-background/40 backdrop-blur-sm flex-shrink-0 flex flex-col min-h-0"
              style={{ width: fileTreeWidth, minWidth: 160 }}
            >
              {sidebarView === 'explorer' ? (
                <FileTree projectName={projectName} webcontainer={webcontainer} />
              ) : (
                <SearchReplacePanel webcontainer={webcontainer} />
              )}
            </div>

            {/* Horizontal resize handle */}
            <div
              className="w-1 flex-shrink-0 cursor-col-resize hover:bg-primary-400/80 bg-transparent transition-colors duration-100 z-10 group"
              onMouseDown={handleFileTreeResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
            >
              <div className="w-px h-full mx-auto bg-border/80 group-hover:bg-primary-400/80 transition-colors duration-100" />
            </div>

            {/* Editor */}
            <div className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col bg-background/70 backdrop-blur-sm">
              <EditorPanel />
            </div>
          </div>
        )}

        {/* Terminal resize strip */}
        <div
          onMouseDown={
            terminalPanelOpen && codeViewMode === 'code' ? handleTerminalResize : undefined
          }
          className={cn(
            'shrink-0 h-1 select-none transition-colors duration-100',
            terminalPanelOpen && codeViewMode === 'code'
              ? 'cursor-row-resize hover:bg-primary-400/50 bg-border/60'
              : 'bg-transparent'
          )}
        />

        {/* Terminal panel */}
        <div
          className={cn(
            'shrink-0 flex flex-col overflow-hidden border-t border-border/50 glass-subtle',
            codeViewMode === 'code' && terminalPanelOpen ? 'bg-background/85' : ''
          )}
          style={{
            height: codeViewMode === 'code' && terminalPanelOpen ? terminalHeight : 0,
            transition: isDraggingPanel ? 'none' : 'height 200ms ease-out',
          }}
          aria-hidden={!(codeViewMode === 'code' && terminalPanelOpen)}
        >
          <div
            className={
              codeViewMode === 'code' && terminalPanelOpen
                ? 'flex-1 min-h-0 h-full'
                : 'h-0 min-h-0 overflow-hidden opacity-0 pointer-events-none [contain:strict]'
            }
          >
            <TerminalTabs />
          </div>
        </div>
      </div>

      {fileSearchOpen && (
        <FileSearch
          files={fileList}
          onSelect={(path) => {
            selectFile(path);
            setFileSearchOpen(false);
            if (codeViewMode !== 'code') setCodeViewMode('code');
          }}
          onClose={() => setFileSearchOpen(false)}
        />
      )}
    </div>
  );
}
